import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { Button, Stack, Loader, Icon, Text } from '@crossfit/ui'

import { useMediaQuery } from 'hooks/useMediaQuery'
import type { Invitee } from 'types'
import * as affiliatesService from 'services/affiliates'
import { useNotification } from 'components/Notification'
import { UserList, UserListItem } from 'components/Admin/Shared/UserList'
import style from './style.module.css'
import { useAffiliate } from 'hooks/useAffiliate'

function useInviteForm(invitees: Invitee[]) {
  const { selectedAffiliate: affiliate } = useAffiliate()
  const navigate = useNavigate()
  const form = useForm()
  const queryClient = useQueryClient()
  const showNotification = useNotification()

  const inviteTeammates = useMutation(
    () =>
      affiliatesService.inviteTeammates({
        affiliateId: affiliate?.id,
        userIds: invitees.map((invitee) => invitee.id),
      }),
    {
      onError: (error: Error) => {
        showNotification(error.message)
      },
    },
  )

  async function handleSubmit() {
    await inviteTeammates.mutateAsync()
    await queryClient.refetchQueries('invitations')

    showNotification(`${invitees.length} invite(s) successfully sent`)
    navigate('/admin/staff/pending')
  }

  return {
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: form.formState.isSubmitting,
  }
}

type Props = {
  invitees: Invitee[]
  onRemove: (index: number) => void
}

function InviteForm({ invitees, onRemove }: Props) {
  const { onSubmit, isSubmitting } = useInviteForm(invitees)
  const isMobile = useMediaQuery(`(max-width: 768px)`)

  return (
    <form onSubmit={onSubmit}>
      {invitees.length > 0 ? (
        <UserList showRole={false} isMobile={isMobile}>
          {invitees.map((invitee, index) => (
            <UserListItem
              key={invitee.id}
              showRole={false}
              name={`${invitee.firstName} ${invitee.lastName}`}
              email={invitee.email}
              certifications={invitee.certifications}
              isMobile={isMobile}
              menu={
                <Button
                  type='button'
                  variant='tertiary'
                  onClick={() => onRemove(index)}
                  className={style.button}
                >
                  <Icon name='close' className={style.buttonIcon} />
                </Button>
              }
            />
          ))}
        </UserList>
      ) : (
        <Text className={style.placeholder} mb={[12, 10]}>
          Enter email addresses above to invite one or more teammates
        </Text>
      )}

      {isSubmitting ? (
        <Loader />
      ) : (
        <Stack
          spacing={5}
          direction={['horizontal', 'vertical']}
          className={style.buttons}
        >
          <Button as={RouterLink} to='/admin/staff' variant='secondary'>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='primary'
            disabled={invitees.length === 0}
          >
            Send Invitations
          </Button>
        </Stack>
      )}
    </form>
  )
}

export default InviteForm
